// Trail effects (launch streaks, intercept beams): ribbons whose head runs along a path over the effect's life.
// Each registry entry gets a reserved range of ribbon slots in the shared RibbonPool. Route sweeps use
// RouteTrailSystem instead.

import type { Color } from 'three'
import { InvariantViolation } from '@taxwar/net'
import { effectsOf, type EffectId, type TrailDef } from './effects'
import { MutablePathEnds, pathLength, samplePath } from './paths'
import type { PathPlacement } from './placement'
import { RIBBON_POINTS, type RibbonPool } from './RibbonPool'
import { SlotRange, scaledCount } from './slots'
import { envelopeAt, trailHead, trailTail } from './timing'

/** Paths shorter than this (render units) have no direction to draw. */
const MIN_PATH_LENGTH = 1e-5
/** A trail whose visible span is shorter than this path fraction is not drawn this frame. */
const MIN_SPAN = 1e-3
const XYZ = 3

/** Ribbon slots the trail effects need at `scale`. */
export function trailSlotCount(scale: number): number {
  let count = 0
  for (const [, def] of effectsOf('trail')) count += scaledCount(def.maxConcurrent, scale)
  return count
}

export class TrailSystem {
  private readonly ranges = new Map<EffectId, SlotRange>()
  /** Indexed by slot − firstSlot. */
  private readonly defs: (TrailDef | null)[]
  private readonly starts: Float64Array
  private readonly lengths: Float32Array
  private readonly ends: readonly MutablePathEnds[]
  /** Live palette colours, kept by reference (see colours.ts). */
  private readonly colours: (Color | null)[]
  private readonly points = new Float32Array(RIBBON_POINTS * XYZ)

  /** Uses ribbon slots [firstSlot, firstSlot + trailSlotCount(scale)). */
  constructor(
    private readonly ribbons: RibbonPool,
    private readonly firstSlot: number,
    scale: number,
  ) {
    let next = 0
    for (const [id, def] of effectsOf('trail')) {
      const count = scaledCount(def.maxConcurrent, scale)
      this.ranges.set(id, new SlotRange(next, count))
      next += count
    }
    if (firstSlot + next > ribbons.capacity) throw new InvariantViolation('trail slots overflow the ribbon pool')
    this.defs = new Array<TrailDef | null>(next).fill(null)
    this.starts = new Float64Array(next)
    this.lengths = new Float32Array(next)
    this.ends = Array.from({ length: next }, () => new MutablePathEnds())
    this.colours = new Array<Color | null>(next).fill(null)
  }

  /** `colour` is a live palette colour: kept by reference, never mutated. */
  play(id: EffectId, def: TrailDef, placement: PathPlacement, colour: Color, now: number): void {
    const range = this.ranges.get(id)
    if (range === undefined) throw new InvariantViolation(`trail effect ${id} has no reserved slots`)
    const index = range.claim()
    const ends = (this.ends[index] as MutablePathEnds).set(placement.from, placement.fromAltitude, placement.to, placement.toAltitude)
    const length = pathLength(def.path, ends)
    this.ribbons.hide(this.firstSlot + index)
    if (length < MIN_PATH_LENGTH) {
      this.defs[index] = null
      this.colours[index] = null
      return
    }
    this.defs[index] = def
    this.starts[index] = now + def.delay
    this.lengths[index] = length
    this.colours[index] = colour
  }

  update(now: number): void {
    for (let index = 0; index < this.defs.length; index++) {
      const def = this.defs[index]
      if (def === null || def === undefined) continue
      const age = now - (this.starts[index] as number)
      if (age < 0) continue
      const slot = this.firstSlot + index
      const t = age / def.duration
      if (t >= 1) {
        this.ribbons.hide(slot)
        this.defs[index] = null
        this.colours[index] = null
        continue
      }
      const head = trailHead(def.head, t)
      const tail = trailTail(head, def.tail)
      if (head - tail < MIN_SPAN) {
        this.ribbons.hide(slot)
        continue
      }
      samplePath(def.path, this.ends[index] as MutablePathEnds, tail, head, this.points)
      const brightness = envelopeAt(def.envelope, t)
      this.ribbons.write(slot, this.points, def.style, this.colours[index] as Color, def.intensity, brightness * def.tailAlpha, brightness, tail * (this.lengths[index] as number))
    }
  }
}
